/**
 * Checklist filtering. Kept out of the component file so that module exports only
 * components (the react-refresh rule the project lints for).
 */
import type { HipaaItem, HipaaPriority, HipaaStatus } from '../types/hipaaTypes';
import { isOpen } from './hipaaStats';

export interface ChecklistFilters {
  /** 'open' is everything not done and not N/A -- the common case, so it gets its own value. */
  status: HipaaStatus | 'all' | 'open';
  priority: HipaaPriority | 'all';
  spec: 'all' | 'R' | 'A';
  flag: 'all' | 'blocked' | 'verify';
  search: string;
}

export const DEFAULT_FILTERS: ChecklistFilters = {
  status: 'all',
  priority: 'all',
  spec: 'all',
  flag: 'all',
  search: '',
};

/**
 * Filters survive in the query string so a filtered view is linkable. Only non-default
 * values are written, which keeps the common URL clean.
 */
export const filtersFromParams = (params: URLSearchParams): ChecklistFilters => ({
  status: (params.get('status') as ChecklistFilters['status']) ?? DEFAULT_FILTERS.status,
  priority: (params.get('priority') as ChecklistFilters['priority']) ?? DEFAULT_FILTERS.priority,
  spec: (params.get('spec') as ChecklistFilters['spec']) ?? DEFAULT_FILTERS.spec,
  flag: (params.get('flag') as ChecklistFilters['flag']) ?? DEFAULT_FILTERS.flag,
  search: params.get('q') ?? DEFAULT_FILTERS.search,
});

export const filtersToParams = (filters: ChecklistFilters): Record<string, string> => {
  const params: Record<string, string> = {};
  if (filters.status !== DEFAULT_FILTERS.status) params.status = filters.status;
  if (filters.priority !== DEFAULT_FILTERS.priority) params.priority = filters.priority;
  if (filters.spec !== DEFAULT_FILTERS.spec) params.spec = filters.spec;
  if (filters.flag !== DEFAULT_FILTERS.flag) params.flag = filters.flag;
  if (filters.search) params.q = filters.search;
  return params;
};

export const matchesFilters = (item: HipaaItem, filters: ChecklistFilters): boolean => {
  if (filters.status === 'open') {
    if (!isOpen(item.status)) return false;
  } else if (filters.status !== 'all' && item.status !== filters.status) {
    return false;
  }

  if (filters.priority !== 'all' && !item.priorities.includes(filters.priority)) return false;
  if (filters.spec !== 'all' && !item.specs.includes(filters.spec)) return false;
  if (filters.flag === 'blocked' && !item.blocked) return false;
  if (filters.flag === 'verify' && !item.needsVerification) return false;

  if (filters.search) {
    const needle = filters.search.toLowerCase();
    const haystack = `${item.title} ${item.statusNote ?? ''} ${item.actions ?? ''} §${item.section}`;
    if (!haystack.toLowerCase().includes(needle)) return false;
  }

  return true;
};
